import { Component, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { first } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  snackbars,
  authMessages,
  passwordValidator,
  emailValidator,
  snackbarsMsg,
} from '@workers';
import { User } from '@shared/models';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UsersService } from '@core/services/users.service';
import {
  MatCard,
  MatCardHeader,
  MatCardTitle,
  MatCardSubtitle,
  MatCardContent,
  MatCardActions,
  MatCardFooter,
} from '@angular/material/card';

import { MatDivider } from '@angular/material/divider';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
  MatError,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatProgressBar } from '@angular/material/progress-bar';

@Component({
  selector: 'app-register-admin-form',
  templateUrl: './register-admin-form.component.html',
  styleUrls: ['./register-admin-form.component.scss'],
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatDivider,
    MatCardContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatIcon,
    MatSuffix,
    MatError,
    MatSelect,
    MatOption,
    MatIconButton,
    MatCardActions,
    MatButton,
    MatCardFooter,
    MatProgressBar,
  ],
})
export class RegisterAdminFormComponent implements OnInit {
  private formBuilder = inject(FormBuilder);

  private snackBar = inject(MatSnackBar);

  private data = inject<User>(MAT_DIALOG_DATA);

  private dialogRef =
    inject<MatDialogRef<RegisterAdminFormComponent>>(MatDialogRef);

  private usersService = inject(UsersService);

  form: FormGroup;

  isUpdate: boolean;

  formInProgress: boolean = false;

  showPassword: boolean;

  authMsg = authMessages;

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', emailValidator],
      password: [null, passwordValidator],
      lang: [null, Validators.required],
    });

    if (this.data) {
      this.isUpdate = true;
      this.form.patchValue(this.data);
      this.password.clearValidators();
    }
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  get firstName(): AbstractControl {
    return this.form.get('firstName') as AbstractControl;
  }

  get lastName(): AbstractControl {
    return this.form.get('lastName') as AbstractControl;
  }

  get email(): AbstractControl {
    return this.form.get('email') as AbstractControl;
  }

  get password(): AbstractControl {
    return this.form.get('password') as AbstractControl;
  }

  submit(): void {
    if (this.formInProgress) {
      this.snackBar.open(snackbarsMsg.formInProgress, '', snackbars.info);
      return;
    }

    this.formInProgress = true;

    if (this.isUpdate) {
      this.adminUpdate();
      return;
    }

    this.adminRegister();
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  private adminRegister(): void {
    this.usersService
      .create(this.form.value)
      .pipe(first())
      .subscribe(
        () => {
          this.snackBar.open(snackbarsMsg.signInSucceed, '', snackbars.success);
          this.formInProgress = false;
          this.cancel();
        },
        () => {
          this.snackBar.open(snackbarsMsg.signInFailed, '', snackbars.error);
          this.formInProgress = false;
        }
      );
  }

  private adminUpdate(): void {
    const formValue = { ...this.form.value };
    if (this.data.email === this.form.value.email) {
      delete formValue.email;
    }
    if (!this.form.value.password) {
      delete formValue.password;
    }

    this.usersService
      .update(this.data.id, formValue)
      .pipe(first())
      .subscribe(
        () => {
          this.snackBar.open(snackbarsMsg.updateSucceed, '', snackbars.success);
          this.formInProgress = false;
          this.cancel();
        },
        () => {
          this.snackBar.open(snackbarsMsg.updateFailed, '', snackbars.error);
          this.formInProgress = false;
        }
      );
  }
}
